import { useState } from 'react';
import { api } from '../api';
import { Banner, Field, Spinner, useAsync } from '../components/Common';
import { formatDateTime } from '../format';

interface AuditRow {
  id: number;
  actor_email: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  detail: any;
  ip: string | null;
  created_at: string;
}

const FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'report.run', label: 'Report runs' },
  { value: 'export', label: 'Exports' },
  { value: 'connection', label: 'Entry point changes' },
  { value: 'request', label: 'Request changes' },
  { value: 'login', label: 'Sign-ins' },
  { value: 'user', label: 'People changes' },
  { value: 'lookup', label: 'List changes' },
];

export function Audit() {
  const [filter, setFilter] = useState('');
  const { data, error, loading } = useAsync(
    () => api.get<AuditRow[]>(`/api/audit?limit=200${filter ? `&action=${encodeURIComponent(filter)}` : ''}`),
    [filter],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Audit log</h1>
          <p>
            Because every Bullhorn call runs through the shared service account, Bullhorn’s own history attributes all of it
            to one robot. This log is the only record of which person did what.
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <Field label="Show">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {loading && !data && <Spinner label="Loading audit entries…" />}

      {data && (
        <div className="card">
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Object</th>
                  <th>Detail</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                    <td>{row.actor_email ?? 'system'}</td>
                    <td className="mono">{row.action}</td>
                    <td>
                      {row.entity ? (
                        <>
                          {row.entity} <span className="mono">{row.entity_id}</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="mono" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.detail ? JSON.stringify(row.detail) : ''}
                    </td>
                    <td className="mono">{row.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.length && <p className="note">Nothing recorded for this filter.</p>}
          <p className="note">Credentials are stripped before anything is written here.</p>
        </div>
      )}
    </>
  );
}
